/**
 * Files data access
 *
 * Handles file metadata creation, workspace links, and retrieval processing.
 *
 * Primary responsibilities:
 * - Create file rows and workspace links
 * - Route browser uploads through authenticated upload APIs
 * - Keep desktop/server storage paths available for trusted clients
 *
 * Dependencies:
 * - Supabase file tables
 * - authenticated upload API
 * - retrieval processing routes
 */

import { TablesInsert, TablesUpdate, Tables } from '@/supabase/types'
import mammoth from 'mammoth'
import { SupabaseClient } from '@supabase/supabase-js'

import { getDesktopAccessTokenHeaders, getDesktopAccessTokenSupabaseClient } from '@/lib/desktop-supabase-client'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { supabase } from '@/lib/supabase/browser-client'
import { isDev } from '@/lib/utils/isDev'
import { getProfileByUserId } from '@/db/profile'
import { validateAttachmentUpload } from '@/lib/subscription'
import type { Database } from '@/supabase/types'

import { uploadFile } from './storage/files'

const resolveFilesClient = (client?: SupabaseClient<Database>) => {
  if (client) return client
  const desktopClient = getDesktopAccessTokenSupabaseClient()
  if (desktopClient) return desktopClient
  if (typeof window === 'undefined') {
    const { createServiceClient } = require('@/lib/supabase/service-client')
    return createServiceClient()
  }
  return supabase
}

type UploadRoutePayload = {
  error?: string
  file?: Tables<'files'>
  message?: string
}

type BlobVideoUploadInitPayload = UploadRoutePayload & {
  pathname?: string
  clientPayload?: string
}

type BlobVideoUploadCompletePayload = UploadRoutePayload

type CreateFileUploadOptions = {
  uploadLimitChatId?: string | null
  uploadLimitSpaceId?: string | null
}

const VIDEO_FILE_EXTENSIONS = ['mp4', 'mov', 'webm', 'mkv', 'avi', 'ogg', 'video']
const MAX_FUNCTION_UPLOAD_BYTES = 4 * 1024 * 1024

const isVideoUploadFile = (file: File, fileRecord: TablesInsert<'files'>) => {
  const fileType = fileRecord.type || file.name.split('.').pop()?.toLowerCase() || ''
  return file.type.startsWith('video/') || VIDEO_FILE_EXTENSIONS.includes(fileType)
}

const shouldUseBlobClientUpload = (file: File, fileRecord: TablesInsert<'files'>) =>
  isVideoUploadFile(file, fileRecord) || file.size > MAX_FUNCTION_UPLOAD_BYTES

const readUploadRoutePayload = async (response: Response) => {
  const responseText = await response.text()

  if (!responseText) {
    return {} satisfies UploadRoutePayload
  }

  try {
    return JSON.parse(responseText) as UploadRoutePayload
  } catch {
    return {
      error: responseText
    } satisfies UploadRoutePayload
  }
}

const resolveUploadRouteErrorMessage = (
  payload: UploadRoutePayload,
  response: Response,
  fallback: string
) => {
  const raw = String(payload.error || payload.message || '').trim()
  const looksLikeHtml =
    raw.startsWith('<') || /<\/?[a-z][\s\S]*>/i.test(raw.slice(0, 200))

  if (raw && !looksLikeHtml && raw.length <= 280) {
    return raw
  }

  if (response.status === 502) {
    return 'Upload failed (502 Bad Gateway). Please try again.'
  }
  if (response.status === 413) {
    return 'File is too large to upload.'
  }
  if (response.status >= 500) {
    return `Upload failed (${response.status}). Please try again.`
  }

  return fallback
}

const readBlobVideoUploadInitPayload = async (response: Response) => {
  return (await readUploadRoutePayload(response)) as BlobVideoUploadInitPayload
}

const readBlobVideoUploadCompletePayload = async (response: Response) => {
  return (await readUploadRoutePayload(response)) as BlobVideoUploadCompletePayload
}

const createFileThroughUploadRoute = async (
  file: File,
  fileRecord: TablesInsert<'files'>,
  workspace_id: string,
  embeddingsProvider: 'openai' | 'local',
  extractedText?: string,
  options: CreateFileUploadOptions = {}
) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('workspace_id', workspace_id)
  formData.append('embeddingsProvider', embeddingsProvider)

  if (fileRecord.user_id) {
    formData.append('user_id', fileRecord.user_id)
  }

  if (fileRecord.description) {
    formData.append('description', fileRecord.description)
  }

  if (fileRecord.sharing) {
    formData.append('sharing', fileRecord.sharing)
  }

  if (fileRecord.folder_id) {
    formData.append('folder_id', fileRecord.folder_id)
  }

  if (options.uploadLimitChatId) {
    formData.append('upload_limit_chat_id', options.uploadLimitChatId)
  }

  if (options.uploadLimitSpaceId) {
    formData.append('upload_limit_space_id', options.uploadLimitSpaceId)
  }

  if (extractedText) {
    formData.append('extractedText', extractedText)
  }

  const response = await authenticatedFetch('/api/upload', {
    method: 'POST',
    body: formData
  })
  const payload = await readUploadRoutePayload(response)

  if (!response.ok || !payload.file) {
    throw new Error(
      resolveUploadRouteErrorMessage(payload, response, 'Failed to upload file')
    )
  }

  // /api/upload now stores bytes and leaves the row 'pending' — heavy
  // processing runs on the async worker (processPendingFiles). DOCX is the one
  // type the worker can't process from stored bytes (its text is extracted in
  // the browser via mammoth), so route it to the docx processor here using the
  // client-extracted text, exactly as before. All other types stay pending for
  // the worker.
  if (extractedText) {
    return await processCreatedFile(payload.file, embeddingsProvider, extractedText)
  }

  return payload.file
}

const createFileThroughBlobUpload = async (
  file: File,
  fileRecord: TablesInsert<'files'>,
  workspace_id: string,
  embeddingsProvider: 'openai' | 'local',
  extractedText?: string,
  options: CreateFileUploadOptions = {}
) => {
  const initResponse = await authenticatedFetch('/api/upload/blob-video', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      action: 'init',
      workspace_id,
      upload_limit_chat_id: options.uploadLimitChatId || undefined,
      upload_limit_space_id: options.uploadLimitSpaceId || undefined,
      embeddingsProvider,
      file: {
        name: fileRecord.name || file.name,
        size: file.size,
        contentType: file.type || 'application/octet-stream',
        description: fileRecord.description || '',
        sharing: fileRecord.sharing || 'private',
        folder_id: fileRecord.folder_id || undefined
      }
    })
  })
  const initPayload = await readBlobVideoUploadInitPayload(initResponse)

  if (!initResponse.ok || !initPayload.file || !initPayload.pathname) {
    throw new Error(
      resolveUploadRouteErrorMessage(
        initPayload,
        initResponse,
        'Failed to prepare file upload'
      )
    )
  }

  try {
    const { upload } = await import('@vercel/blob/client')
    const blob = await upload(initPayload.pathname, file, {
      access: 'public',
      handleUploadUrl: '/api/upload/blob-video',
      clientPayload: initPayload.clientPayload,
      contentType: file.type || 'application/octet-stream',
      multipart: true
    })

    const completeResponse = await authenticatedFetch('/api/upload/blob-video', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'complete',
        fileId: initPayload.file.id,
        blob: {
          pathname: blob.pathname,
          url: blob.url
        }
      })
    })
    const completePayload = await readBlobVideoUploadCompletePayload(completeResponse)

    if (!completeResponse.ok || !completePayload.file) {
      throw new Error(
        resolveUploadRouteErrorMessage(
          completePayload,
          completeResponse,
          'Failed to finalize file upload'
        )
      )
    }

    return await processCreatedFile(completePayload.file, embeddingsProvider, extractedText)
  } catch (error) {
    await updateFile(initPayload.file.id, { processing_status: 'failed' }).catch(() => {})
    throw error
  }
}

const processCreatedFile = async (
  createdFile: Tables<'files'>,
  embeddingsProvider: 'openai' | 'local',
  extractedText?: string,
  client?: SupabaseClient<Database>
) => {
  const filesClient = resolveFilesClient(client)

  if (extractedText) {
    const response = await fetch('/api/retrieval/process/docx', {
      method: 'POST',
      headers: await getDesktopAccessTokenHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        text: extractedText,
        fileId: createdFile.id,
        embeddingsProvider,
        fileExtension: 'docx'
      })
    })

    if (!response.ok) {
      let message = 'Failed to process file'
      try {
        const errorData = await response.json()
        message = errorData.message || message
      } catch {
        try {
          message = await response.text()
        } catch {}
      }
      await updateFile(createdFile.id, { processing_status: 'failed' }, filesClient)
      throw new Error(message)
    }
  } else {
    const formData = new FormData()
    formData.append('file_id', createdFile.id)
    formData.append('embeddingsProvider', embeddingsProvider)

    const response = await fetch('/api/retrieval/process', {
      method: 'POST',
      headers: await getDesktopAccessTokenHeaders(),
      body: formData
    })

    if (!response.ok) {
      let message = 'Failed to process file'
      try {
        const errorData = await response.json()
        message = errorData.message || message
      } catch {
        try {
          message = await response.text()
        } catch {}
      }
      await updateFile(createdFile.id, { processing_status: 'failed' }, filesClient)
      throw new Error(message)
    }
  }

  return await getFileById(createdFile.id, filesClient)
}

export const getFileById = async (
  fileId: string,
  client?: SupabaseClient<Database>
) => {
  const filesClient = resolveFilesClient(client)
  const { data: file, error } = await filesClient
    .from('files')
    .select('*, file_items (*)')
    .eq('id', fileId)
    .single()

  if (!file) {
    throw new Error(error?.message || 'File not found')
  }

  return file
}

export type FileProcessingPollResult = 'completed' | 'failed' | 'pending' | 'timeout'

/**
 * Poll a file's processing_status until it leaves the pending/processing state
 * or the timeout elapses. Lightweight (selects only id/processing_status) and
 * client-safe (browser client is RLS-scoped to the user's own files).
 *
 * Returns 'completed'/'failed' on resolution, or 'timeout' if still processing
 * when the budget runs out — callers treat 'timeout' as "let the user send
 * anyway" so a slow worker never permanently blocks the composer.
 */
export const waitForFileProcessing = async (
  fileId: string,
  options?: {
    timeoutMs?: number
    intervalMs?: number
    client?: SupabaseClient<Database>
  }
): Promise<FileProcessingPollResult> => {
  const timeoutMs = options?.timeoutMs ?? 120_000
  const intervalMs = options?.intervalMs ?? 1_500
  const filesClient = resolveFilesClient(options?.client)
  const deadline = Date.now() + timeoutMs

  while (Date.now() < deadline) {
    const { data } = await filesClient
      .from('files')
      .select('processing_status')
      .eq('id', fileId)
      .maybeSingle()

    const status = (data?.processing_status as string | undefined) ?? 'pending'
    if (status === 'completed') return 'completed'
    if (status === 'failed') return 'failed'

    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }

  return 'timeout'
}

export const getAllPublicHtmlFiles = async () => {
  const { data: files, error } = await supabase
    .from('files')
    .select('id, hashid, name, description, file_items (content)')
    .eq('type', 'html')
    .eq('sharing', 'public')
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(error.message)
  }

  return files
}

export const getFileByHashId = async (
  hashId: string,
  client?: SupabaseClient<Database>
) => {
  const filesClient = resolveFilesClient(client)
  const { data: file, error } = await filesClient
    .from('files')
    .select('*, file_items (*)')
    .eq('hashid', hashId)
    .single()

  if (!file) {
    throw new Error(error?.message || 'File not found')
  }

  return file
}

export const getFileWorkspacesByWorkspaceId = async (workspaceId: string) => {
  const { data: workspace, error } = await supabase
    .from('workspaces')
    .select(
      `
      id,
      name,
      files (*)
    `
    )
    .eq('id', workspaceId)
    .single()

  if (!workspace) {
    throw new Error(error.message)
  }

  return workspace
}

export const getFileWorkspacesByFileId = async (fileId: string) => {
  const { data: file, error } = await supabase
    .from('files')
    .select(
      `
      id, 
      name, 
      workspaces (*)
    `
    )
    .eq('id', fileId)
    .single()

  if (!file) {
    throw new Error(error.message)
  }

  return file
}

export const createFile = async (
  file: File,
  fileRecord: TablesInsert<'files'>,
  workspace_id: string,
  embeddingsProvider: 'openai' | 'local',
  extractedText?: string,
  options: CreateFileUploadOptions = {}
) => {
  const shouldUseUploadRoute =
    typeof window !== 'undefined' && !getDesktopAccessTokenSupabaseClient()

  if (shouldUseUploadRoute) {
    if (shouldUseBlobClientUpload(file, fileRecord)) {
      return await createFileThroughBlobUpload(
        file,
        fileRecord,
        workspace_id,
        embeddingsProvider,
        extractedText,
        options
      )
    }

    return await createFileThroughUploadRoute(
      file,
      fileRecord,
      workspace_id,
      embeddingsProvider,
      extractedText,
      options
    )
  }

  const profile = fileRecord.user_id ? await getProfileByUserId(fileRecord.user_id) : null
  const validation = validateAttachmentUpload(profile, [{ size: file.size }])
  if (!validation.ok) {
    throw new Error(validation.message)
  }

  const filesClient = resolveFilesClient()
  const { data: createdFile, error } = await filesClient
    .from('files')
    .insert([
      {
        ...fileRecord,
        processing_status: 'pending'
      }
    ])
    .select('*')
    .single()

  if (error) {
    isDev && console.error('Error creating file:', error)
    throw new Error(error.message)
  }

  try {
    await createFileWorkspace({
      user_id: createdFile.user_id,
      file_id: createdFile.id,
      workspace_id
    }, filesClient)

    const filePath = await uploadFile(file, {
      name: createdFile.name,
      user_id: createdFile.user_id,
      file_id: createdFile.id
    }, filesClient)

    await updateFile(createdFile.id, {
      file_path: filePath,
      processing_status: 'processing'
    }, filesClient)

    return await processCreatedFile(createdFile, embeddingsProvider, extractedText, filesClient)
  } catch (error) {
    isDev && console.error('Error processing file:', error)
    await updateFile(createdFile.id, { processing_status: 'failed' }, filesClient)
    throw error
  }
}

export const createFileFromStoragePath = async (
  fileRecord: TablesInsert<'files'>,
  workspace_id: string,
  embeddingsProvider: 'openai' | 'local',
  filePath: string
) => {
  if (!filePath) {
    throw new Error('Missing storage path for desktop attachment')
  }

  const filesClient = resolveFilesClient()
  const { data: createdFile, error } = await filesClient
    .from('files')
    .insert([
      {
        ...fileRecord,
        file_path: filePath,
        processing_status: 'pending'
      }
    ])
    .select('*')
    .single()

  if (error) {
    isDev && console.error('Error creating file from storage path:', error)
    throw new Error(error.message)
  }

  try {
    await createFileWorkspace({
      user_id: createdFile.user_id,
      file_id: createdFile.id,
      workspace_id
    }, filesClient)

    return await processCreatedFile(createdFile, embeddingsProvider, undefined, filesClient)
  } catch (error) {
    isDev && console.error('Error processing existing storage file:', error)
    await updateFile(createdFile.id, { processing_status: 'failed' }, filesClient)
    throw error
  }
}

export const createFiles = async (files: TablesInsert<'files'>[], workspace_id: string) => {
  const filesClient = resolveFilesClient()
  const { data: createdFiles, error } = await filesClient.from('files').insert(files).select('*')

  if (error) {
    throw new Error(error.message)
  }

  await createFileWorkspaces(
    createdFiles.map((file) => ({
      user_id: file.user_id,
      file_id: file.id,
      workspace_id
    })),
    filesClient
  )

  return createdFiles
}

export const createFileWorkspace = async (
  item: {
    user_id: string
    file_id: string
    workspace_id: string
  },
  client?: SupabaseClient<Database>
) => {
  const filesClient = resolveFilesClient(client)
  const { data: createdFileWorkspace, error } = await filesClient
    .from('file_workspaces')
    .insert([item])
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return createdFileWorkspace
}

export const createFileWorkspaces = async (
  items: { user_id: string; file_id: string; workspace_id: string }[],
  client?: SupabaseClient<Database>
) => {
  const filesClient = resolveFilesClient(client)
  const { data: createdFileWorkspaces, error } = await filesClient
    .from('file_workspaces')
    .insert(items)
    .select('*')

  if (error) throw new Error(error.message)

  return createdFileWorkspaces
}

export const updateFile = async (
  fileId: string,
  file: TablesUpdate<'files'>,
  client?: SupabaseClient<Database>
) => {
  const filesClient = resolveFilesClient(client)
  const { data: updatedFile, error } = await filesClient
    .from('files')
    .update(file)
    .eq('id', fileId)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return updatedFile
}

export const deleteFile = async (fileId: string) => {
  const filesClient = resolveFilesClient()
  const { error } = await filesClient.from('files').delete().eq('id', fileId)

  if (error) {
    throw new Error(error.message)
  }

  return true
}

export const deleteFileWorkspace = async (fileId: string, workspaceId: string) => {
  const { error } = await supabase
    .from('file_workspaces')
    .delete()
    .eq('file_id', fileId)
    .eq('workspace_id', workspaceId)

  if (error) throw new Error(error.message)

  return true
}

export const copyFileAndFileItems = async (
  fileId: string,
  workspaceId: string,
  userId: string,
  client = supabase
) => {
  // Fetch the original file
  const originalFile = await getFileById(fileId, client)

  // Check if the file is public
  if (originalFile.sharing !== 'public') {
    throw new Error('Cannot copy a non-public file')
  }

  // Prepare the new file record
  const newFileRecord: TablesInsert<'files'> = {
    name: `Copy of ${originalFile.name}`,
    type: originalFile.type,
    sharing: 'private', // Set the new copy to private by default
    user_id: userId,
    description: originalFile.description,
    file_path: '',
    tokens: originalFile.tokens,
    size: originalFile.size
  }

  // Create the new file
  const { data: newFile, error: newFileError } = await client
    .from('files')
    .insert([newFileRecord])
    .select('*')
    .single()

  if (newFileError) {
    throw new Error(newFileError.message)
  }

  const newFileItems = originalFile.file_items.map(
    (item: { content: string; tokens: number; openai_embedding: any; local_embedding: any }) => ({
      content: item.content,
      tokens: item.tokens,
      openai_embedding: item.openai_embedding,
      local_embedding: item.local_embedding,
      user_id: userId,
      sharing: 'private',
      file_id: newFile.id
    })
  )

  const { data: insertedFileItems, error: insertError } = await client
    .from('file_items')
    .insert(newFileItems)
    .select('*')

  if (insertError) {
    throw new Error(insertError.message)
  }

  // Create file workspace association
  await createFileWorkspace(
    {
      user_id: userId,
      file_id: newFile.id,
      workspace_id: workspaceId
    },
    client
  )

  // If there's a file_path, we need to copy the actual file in storage
  if (originalFile.file_path) {
    const { data: fileData, error: downloadError } = await client.storage
      .from('files')
      .download(originalFile.file_path)

    if (downloadError) {
      isDev && console.error(downloadError)
      return { ...newFile, file_items: insertedFileItems }
      // throw new Error(downloadError.message)
    }

    const newFilePath = await uploadFile(
      fileData,
      {
        name: newFile.name,
        user_id: userId,
        file_id: newFile.id
      },
      client
    )

    await updateFile(
      newFile.id,
      {
        file_path: newFilePath
      },
      client
    )
  }

  return { ...newFile, file_items: insertedFileItems }
}

export const getFilesWithGeneratedImages = async (userId: string) => {
  const { data: files, error: filesError } = await supabase
    .from('files')
    .select('*')
    .eq('user_id', userId)

  if (filesError) {
    throw new Error(filesError.message)
  }

  // Generated images live in public bucket at root (e.g. m1ceyb7b.png);
  // list root and use object name as file_path for public URL resolution.
  const { data: generatedImages, error: imagesError } = await supabase.storage
    .from('generated_images')
    .list('', { limit: 1000 })

  if (imagesError) {
    throw new Error(imagesError.message)
  }

  const imageObjects =
    generatedImages?.filter((o) => o.name && !o.name.endsWith('/')) ?? []
  const generatedImageFiles = imageObjects.map((image) => ({
    id: image.id ?? image.name,
    user_id: userId,
    name: image.name,
    description: 'Generated Image',
    file_path: image.name,
    type: 'generated-image' as const,
    size: (image.metadata as { size?: number } | undefined)?.size ?? 0,
    tokens: 0,
    created_at: image.created_at ?? new Date().toISOString(),
    updated_at: image.updated_at ?? new Date().toISOString(),
    sharing: 'private' as const
  }))

  // Generated videos in public bucket at root (e.g. abc123.mp4); exclude _meta.json
  const { data: generatedVideos, error: videosError } = await supabase.storage
    .from('generated_videos')
    .list('', { limit: 1000 })

  if (videosError) {
    throw new Error(videosError.message)
  }

  const videoObjects =
    generatedVideos?.filter(
      (o) => o.name && !o.name.endsWith('/') && !o.name.endsWith('_meta.json')
    ) ?? []
  const generatedVideoFiles = videoObjects.map((video) => ({
    id: video.id ?? video.name,
    user_id: userId,
    name: video.name,
    description: 'Generated Video',
    file_path: video.name,
    type: 'generated-video' as const,
    size: (video.metadata as { size?: number } | undefined)?.size ?? 0,
    tokens: 0,
    created_at: video.created_at ?? new Date().toISOString(),
    updated_at: video.updated_at ?? new Date().toISOString(),
    sharing: 'private' as const
  }))

  return [...files, ...generatedImageFiles, ...generatedVideoFiles]
}


// --------------

export type ClaimedPendingFile = {
  id: string
  user_id: string
}

/**
 * Atomically claims up to `limit` files sitting at processing_status='pending'
 * and flips them to 'processing', returning the rows this call actually won.
 *
 * Server-only: always uses the Supabase service client (never the browser
 * client), matching the claim pattern in db/chat-tool-runs.ts and
 * db/nexus-pending-retries.ts. Each candidate is claimed with a conditional
 * UPDATE guarded by `.eq('processing_status', 'pending')`, so two concurrent
 * workers can't both claim the same row — only the first worker's UPDATE
 * matches; the loser's filter no longer matches once the row is 'processing'.
 */
export const claimPendingFiles = async ({
  limit
}: {
  limit: number
}): Promise<ClaimedPendingFile[]> => {
  if (typeof window !== 'undefined') {
    throw new Error('claimPendingFiles is server-only')
  }

  const { createServiceClient } = require('@/lib/supabase/service-client')
  const client = createServiceClient() as SupabaseClient<Database>

  // A file is claimable if it is 'pending', OR it is 'processing' but has sat
  // untouched longer than the stale threshold — meaning a prior worker leg
  // claimed it then died mid-batch (e.g. a provider 429 storm) and left it
  // stranded. Without this reclaim, such a file is stuck forever ('processing'
  // is never re-claimed) and the chat retrieval verify endpoint waits on it
  // indefinitely → the model reports "no files came through".
  const STALE_PROCESSING_MS = 5 * 60 * 1000
  const staleBefore = new Date(Date.now() - STALE_PROCESSING_MS).toISOString()

  // Only claim real chat/workspace FILE UPLOADS. Other systems also write to
  // the `files` table in a 'processing' state with their own pipelines — most
  // notably Space notes (file_path like 'space-notes/…', vectorized elsewhere)
  // — and the worker was hanging trying to process those via the upload
  // signed-URL path. Require a non-empty file_path and exclude the space-notes
  // prefix so we never touch another pipeline's rows.
  const { data: pendingRows, error: pendingError } = await client
    .from('files')
    .select('id, user_id')
    .eq('processing_status', 'pending')
    .not('file_path', 'is', null)
    .neq('file_path', '')
    .not('file_path', 'like', 'space-notes/%')
    .order('created_at', { ascending: true })
    .limit(limit)

  if (pendingError) {
    throw new Error(pendingError.message)
  }

  const candidates = [...((pendingRows ?? []) as ClaimedPendingFile[])]

  // Top up with stale 'processing' rows if there is remaining batch budget.
  if (candidates.length < limit) {
    const { data: staleRows, error: staleError } = await client
      .from('files')
      .select('id, user_id')
      .eq('processing_status', 'processing')
      .lt('updated_at', staleBefore)
      .not('file_path', 'is', null)
      .neq('file_path', '')
      .not('file_path', 'like', 'space-notes/%')
      .order('updated_at', { ascending: true })
      .limit(limit - candidates.length)

    if (staleError) {
      throw new Error(staleError.message)
    }

    candidates.push(...((staleRows ?? []) as ClaimedPendingFile[]))
  }

  const claimed: ClaimedPendingFile[] = []

  for (const candidate of candidates) {
    // Re-stamp updated_at on claim so (a) two workers can't both win — the
    // conditional guard only matches a row still eligible — and (b) a freshly
    // claimed row is not immediately re-eligible as "stale" by another leg.
    const { data: claimedRow, error: claimError } = await client
      .from('files')
      .update({
        processing_status: 'processing',
        updated_at: new Date().toISOString()
      })
      .eq('id', candidate.id)
      // Claim it whether it was pending or a stale processing row, but ONLY if
      // nothing else has touched it since we read it (guards the race): a row
      // that another worker already re-stamped will have a newer updated_at and
      // not match either branch below.
      .or(
        `processing_status.eq.pending,and(processing_status.eq.processing,updated_at.lt.${staleBefore})`
      )
      .select('id, user_id')
      .maybeSingle()

    if (claimError) {
      throw new Error(claimError.message)
    }

    if (claimedRow) {
      claimed.push(claimedRow as ClaimedPendingFile)
    }
  }

  return claimed
}
